import React, { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import { tokens } from '../../../theme';
import { useGetProductsQuery } from '../../../features/products/productsApiSlice';
import ProductCard from '../store-components/ProductCard';

const getProductsArray = (productsData) => {
  if (Array.isArray(productsData)) return productsData;
  return productsData?.products || [];
};

const StoreList = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const isMobile = useMediaQuery('(max-width:700px)');

  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedFitment, setSelectedFitment] = useState('all');
  const [search, setSearch] = useState('');

  const {
    data: productsData,
    isLoading,
    isFetching,
    isError,
    error,
  } = useGetProductsQuery({
    status: 'active',
    includeArchived: false,
    limit: 100,
  });

  const products = getProductsArray(productsData);

  const categories = useMemo(() => {
    const unique = new Set(products.map((product) => product.category).filter(Boolean));
    return ['all', ...Array.from(unique)];
  }, [products]);

  const fitmentOptions = useMemo(() => {
    const unique = new Set();

    products.forEach((product) => {
      product.fitment?.forEach((fitment) => {
        if (fitment?.atgModel) unique.add(fitment.atgModel);
      });
    });

    return ['all', ...Array.from(unique)];
  }, [products]);

  const filteredProducts = useMemo(() => {
    const normalizedSearch = search.trim().toLowerCase();

    return products.filter((product) => {
      const matchesCategory =
        selectedCategory === 'all' || product.category === selectedCategory;

      const matchesFitment =
        selectedFitment === 'all' ||
        product.fitment?.some((fitment) => fitment.atgModel === selectedFitment);

      const searchableText = [
        product.name,
        product.slug,
        product.sku,
        product.category,
        product.tagline,
        product.shortDescription,
        product.description,
        ...(product.features || []).map((feature) => feature.title),
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();

      const matchesSearch =
        !normalizedSearch || searchableText.includes(normalizedSearch);

      return matchesCategory && matchesFitment && matchesSearch;
    });
  }, [products, selectedCategory, selectedFitment, search]);

  return (
    <Box>
      <Container maxWidth="xl">
        <Stack spacing={2} textAlign="center" mb={5}>
          <Chip
            label="USFuel Product Catalog"
            sx={{
              alignSelf: 'center',
              color: colors.greenAccent[300],
              borderColor: colors.greenAccent[600],
              backgroundColor: 'rgba(0,255,170,0.08)',
              fontWeight: 700,
            }}
            variant="outlined"
          />

          <Typography
            variant={isMobile ? 'h3' : 'h2'}
            fontWeight="900"
            sx={{
              color: 'white',
              fontFamily: 'Poppins, sans-serif',
              letterSpacing: '-0.04em',
            }}
          >
            Modern ATG hardware and compliance software
          </Typography>

          <Typography
            variant="h6"
            sx={{
              color: colors.grey[300],
              maxWidth: '850px',
              mx: 'auto',
              lineHeight: 1.6,
              fontFamily: 'Poppins, sans-serif',
            }}
          >
            Upgrade legacy ATGs with touchscreen control, remote monitoring,
            automated reporting, fuel sales reconciliation, and technician tooling.
          </Typography>
        </Stack>

        <Paper
          elevation={0}
          sx={{
            p: { xs: 2, md: 3 },
            mb: 5,
            borderRadius: '24px',
            backgroundColor: 'rgba(255,255,255,0.055)',
            border: '1px solid rgba(255,255,255,0.10)',
            backdropFilter: 'blur(12px)',
          }}
        >
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={5}>
              <TextField
                fullWidth
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search products, features, fitment..."
                InputProps={{
                  startAdornment: (
                    <SearchOutlinedIcon sx={{ mr: 1, color: colors.grey[400] }} />
                  ),
                }}
                sx={{
                  '& .MuiInputBase-root': {
                    color: 'white',
                    backgroundColor: 'rgba(0,0,0,0.25)',
                    borderRadius: '14px',
                  },
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'rgba(255,255,255,0.14)',
                  },
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth>
                <InputLabel sx={{ color: colors.grey[300] }}>Category</InputLabel>
                <Select
                  value={selectedCategory}
                  label="Category"
                  onChange={(e) => setSelectedCategory(e.target.value)}
                  sx={{
                    color: 'white',
                    backgroundColor: 'rgba(0,0,0,0.25)',
                    borderRadius: '14px',
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'rgba(255,255,255,0.14)',
                    },
                  }}
                >
                  {categories.map((category) => (
                    <MenuItem key={category} value={category}>
                      {category === 'all' ? 'All Categories' : category}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth>
                <InputLabel sx={{ color: colors.grey[300] }}>ATG Fitment</InputLabel>
                <Select
                  value={selectedFitment}
                  label="ATG Fitment"
                  onChange={(e) => setSelectedFitment(e.target.value)}
                  sx={{
                    color: 'white',
                    backgroundColor: 'rgba(0,0,0,0.25)',
                    borderRadius: '14px',
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'rgba(255,255,255,0.14)',
                    },
                  }}
                >
                  {fitmentOptions.map((fitment) => (
                    <MenuItem key={fitment} value={fitment}>
                      {fitment === 'all' ? 'All Fitment' : fitment}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={1}>
              <Button
                fullWidth
                variant="outlined"
                onClick={() => {
                  setSearch('');
                  setSelectedCategory('all');
                  setSelectedFitment('all');
                }}
                sx={{
                  height: '56px',
                  borderRadius: '14px',
                  color: colors.greenAccent[300],
                  borderColor: colors.greenAccent[600],
                }}
              >
                Reset
              </Button>
            </Grid>
          </Grid>
        </Paper>

        {isLoading || isFetching ? (
          <Box display="flex" justifyContent="center" py={8}>
            <CircularProgress />
          </Box>
        ) : isError ? (
          <Alert severity="error">
            {error?.data?.message || error?.message || 'Failed to load products.'}
          </Alert>
        ) : filteredProducts.length === 0 ? (
          <Paper
            sx={{
              p: 5,
              textAlign: 'center',
              borderRadius: '20px',
              backgroundColor: 'rgba(255,255,255,0.055)',
              color: 'white',
            }}
          >
            <Typography variant="h4" fontWeight="bold">
              No products found
            </Typography>
            <Typography color={colors.grey[300]} mt={1}>
              Try changing your filters or search.
            </Typography>
          </Paper>
        ) : (
          <Grid container spacing={3}>
            {filteredProducts.map((product) => (
              <Grid item xs={12} sm={6} lg={4} key={product._id || product.slug}>
                <ProductCard product={product} />
              </Grid>
            ))}
          </Grid>
        )}
      </Container>
    </Box>
  );
};

export default StoreList;